import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { isMobileBrowser } from '../utils/scroll.util';

const SCROLL_VISIBILITY_THRESHOLD_PX = 80;

@Injectable({ providedIn: 'root' })
export class StickyZipCtaService {
  readonly visible = signal(false);

  private enabled = false;
  private anchorEl: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private scrollMode = false;
  private scrollListener: (() => void) | null = null;
  private scrollModeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  isRouteEnabled(urlPath: string): boolean {
    const path = urlPath.split('?')[0];
    return !path.startsWith('/questionnaire');
  }

  onRouteChange(urlPath: string): void {
    this.clearObservers();
    this.visible.set(false);

    if (!isPlatformBrowser(this.platformId)) {
      this.enabled = false;
      return;
    }

    this.enabled = this.isRouteEnabled(urlPath);
    if (!this.enabled) {
      return;
    }

    this.scheduleScrollModeFallback();
  }

  registerAnchor(element: HTMLElement): void {
    if (!this.enabled || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.cancelScrollModeFallback();
    this.clearAnchorObserver();
    this.disableScrollMode();

    this.anchorEl = element;
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        this.visible.set(!entry.isIntersecting);
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    this.intersectionObserver.observe(element);
  }

  unregisterAnchor(element: HTMLElement): void {
    if (this.anchorEl !== element) {
      return;
    }

    this.clearAnchorObserver();
    if (this.enabled) {
      this.enableScrollMode();
    }
  }

  private scheduleScrollModeFallback(): void {
    this.cancelScrollModeFallback();
    const delayMs = isMobileBrowser() ? 400 : 150;
    this.scrollModeTimer = setTimeout(() => {
      if (this.enabled && !this.anchorEl) {
        this.enableScrollMode();
      }
    }, delayMs);
  }

  private cancelScrollModeFallback(): void {
    if (this.scrollModeTimer != null) {
      clearTimeout(this.scrollModeTimer);
      this.scrollModeTimer = null;
    }
  }

  private enableScrollMode(): void {
    if (!isPlatformBrowser(this.platformId) || this.scrollMode || this.anchorEl) {
      return;
    }

    this.scrollMode = true;

    this.scrollListener = () => this.updateScrollVisibility();
    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  private disableScrollMode(): void {
    if (!this.scrollListener) {
      this.scrollMode = false;
      return;
    }

    window.removeEventListener('scroll', this.scrollListener);
    this.scrollListener = null;
    this.scrollMode = false;
  }

  private updateScrollVisibility(): void {
    if (!this.scrollMode) {
      return;
    }

    this.visible.set(window.scrollY > SCROLL_VISIBILITY_THRESHOLD_PX);
  }

  private clearAnchorObserver(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.anchorEl = null;
  }

  private clearObservers(): void {
    this.cancelScrollModeFallback();
    this.clearAnchorObserver();
    this.disableScrollMode();
    this.enabled = false;
  }
}
